import { Formik, type FormikProps } from 'formik'
import React, { useRef } from 'react'

import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'

import { useShopShippingFlatRatesQuery } from '../../../../hooks/useShopShippingFlatRatesQuery'
import type { FormValues } from './Shipping.types'
import { ShippingView } from './ShippingView'
import { validationSchema } from './consts'
import { useSaveFlatRatesMutation } from './queries/useSaveFlatRatesMutation'
import { useSetShippingProfilesStateMutation } from './queries/useSetShippingProfilesStateMutation'
import { useShippingProfilesQuery } from './queries/useShippingProfilesQuery'
import { useUpdateShopOriginLocationMutation } from './queries/useUpdateShopOriginLocationMutation'
import { getInitialValues } from './utils/getInitialValues'
import { getSaveFlatRatesPayload } from './utils/getSaveFlatRatesPayload'

export const ShippingContainer = () => {
  const { currentShopQuery } = useCurrentShopQuery()
  const { shopShippingFlatRatesQuery } = useShopShippingFlatRatesQuery()
  const { saveFlatRatesMutation } = useSaveFlatRatesMutation()
  const { updateShopOriginLocationMutation } = useUpdateShopOriginLocationMutation(
    currentShopQuery.data?.id!,
  )
  const { setShippingProfilesStateMutation } = useSetShippingProfilesStateMutation()
  const flatRates = shopShippingFlatRatesQuery.data
  const { shippingProfilesQuery, invalidateShippingProfilesQuery } = useShippingProfilesQuery()
  const formRef = useRef<FormikProps<FormValues>>(null)

  const initialValues = getInitialValues(
    flatRates,
    currentShopQuery.data,
    shippingProfilesQuery.data,
  )

  const shippingPricingMethodOptions = [
    { label: 'Fixed shipping rates', value: 'Fixed' },
    { label: 'Estimate based on order weight', value: 'Weighted' },
  ]

  const handleSubmit = (values: FormValues) => {
    saveFlatRatesMutation.mutate(getSaveFlatRatesPayload(values))

    if (values.shippingLocation) {
      updateShopOriginLocationMutation.mutate(values.shippingLocation)
    }

    if (values.withShippingGroupsYouShip !== initialValues.withShippingGroupsYouShip) {
      if (
        values.withShippingGroupsYouShip &&
        !shippingProfilesQuery.data?.shippingProfiles.length
      ) {
        formRef.current?.setFieldValue('withShippingGroupsYouShip', false)
      } else {
        setShippingProfilesStateMutation.mutate(
          [
            {
              enabled: values.withShippingGroupsYouShip,
            },
          ],
          {
            onSuccess: invalidateShippingProfilesQuery,
          },
        )
      }
    }
  }

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <ShippingView
        shippingPricingMethodOptions={shippingPricingMethodOptions}
        isLoading={shopShippingFlatRatesQuery.isFetching || shippingProfilesQuery.isFetching}
        isSubmitting={
          saveFlatRatesMutation.isLoading ||
          updateShopOriginLocationMutation.isLoading ||
          setShippingProfilesStateMutation.isLoading
        }
      />
    </Formik>
  )
}
